// @ts-check
"use strict";

/** @typedef {readonly (string | number)[]} ContractPath */
/** @typedef {Record<string, unknown>} JsonObject */
/** @typedef {import("./learning").ElearningCollectionsV1} ElearningCollectionsV1 */
/** @typedef {import("./learning").ElearningCourseMapV1} ElearningCourseMapV1 */
/** @typedef {import("./learning").ElearningCourseV1} ElearningCourseV1 */
/** @typedef {import("./learning").ElearningHashV1} ElearningHashV1 */
/** @typedef {import("./learning").ElearningLearningPathMapV1} ElearningLearningPathMapV1 */
/** @typedef {import("./learning").ElearningLearningPathV1} ElearningLearningPathV1 */
/** @typedef {import("./learning").ElearningMediaRefV1} ElearningMediaRefV1 */
/** @typedef {import("./learning").ElearningTemplateKindV1} ElearningTemplateKindV1 */
/** @typedef {import("./learning").ElearningTemplatePageV1} ElearningTemplatePageV1 */
/** @typedef {import("./learning").ElearningUuid} ElearningUuid */
/** @typedef {import("./learning").LearningHtmlSanitizer} LearningHtmlSanitizer */
/** @typedef {import("./learning").LearningHtmlSanitizerOptions} LearningHtmlSanitizerOptions */
/** @typedef {import("./learning").ResolveElearningMedia} ResolveElearningMedia */
/** @typedef {import("./learning").SafeElearningTemplatePage<unknown>} UnknownKindSafeTemplatePage */

const ELEARNING_LOCALE = "de-DE";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const HASH_PATTERN = /^sha256:[0-9a-f]{64}$/;
const MEDIA_FILENAME_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9]+$/;
const RESOLVED_MEDIA_URI_PATTERN = /^(?:https?:\/\/|file:\/\/|content:\/\/)[^\s]+$/i;
const WEB_LINK_PATTERN = /^https?:\/\/[^\s]+$/i;
const TEMPLATE_KINDS = new Set([
  "textOnly",
  "imageFullscreen",
  "twoImagesFullscreen",
  "imageLeftTextRight",
  "imageRightTextLeft",
  "floatingImageRight",
  "floatingImageLeft",
  "singlePage",
]);

/** @type {LearningHtmlSanitizerOptions} */
const LEARNING_HTML_OPTIONS = {
  allowedTags: [
    "p",
    "span",
    "body",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "ul",
    "ol",
    "li",
    "b",
    "strong",
    "em",
    "i",
    "br",
    "a",
  ],
  allowedAttributes: { a: ["href"] },
  allowedSchemes: ["http", "https"],
  allowProtocolRelative: false,
  enforceHtmlBoundary: true,
  transformTags: {
    a: (_tagName, attributes) => {
      const href = attributes.href;
      return WEB_LINK_PATTERN.test(href || "")
        ? { tagName: "a", attribs: { href } }
        : { tagName: "span", attribs: /** @type {Record<string, string>} */ ({}) };
    },
  },
};

/**
 * @param {ContractPath} path
 * @param {string | number} segment
 * @returns {ContractPath}
 */
const childPath = (path, segment) => [...path, segment];

/**
 * @param {ContractPath} path
 * @returns {string}
 */
const formatPath = path => {
  let result = "$";
  path.forEach(segment => {
    result = typeof segment === "number" ? `${result}[${segment}]` : `${result}[${JSON.stringify(segment)}]`;
  });
  return result;
};

class ElearningContentContractError extends Error {
  /**
   * @param {ContractPath} path
   * @param {string} message
   */
  constructor(path, message) {
    const formattedPath = formatPath(path);
    super(`${formattedPath}: ${message}`);
    this.name = "ElearningContentContractError";
    this.path = formattedPath;
  }
}

/**
 * @param {ContractPath} path
 * @param {string} message
 * @returns {never}
 */
const fail = (path, message) => {
  throw new ElearningContentContractError(path, message);
};

/**
 * @param {object} value
 * @param {PropertyKey} key
 * @returns {boolean}
 */
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

/**
 * @param {unknown} value
 * @param {ContractPath} path
 * @returns {JsonObject}
 */
const expectRecord = (value, path) => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    fail(path, "expected an object");
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    fail(path, "expected a plain object");
  }
  return /** @type {JsonObject} */ (value);
};

/**
 * @param {JsonObject} value
 * @param {ContractPath} path
 * @returns {string[]}
 */
const ownJsonKeys = (value, path) =>
  Reflect.ownKeys(value).map(key => {
    if (typeof key !== "string") {
      fail(path, "symbol keys are not valid JSON object keys");
    }
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor || !descriptor.enumerable || !("value" in descriptor)) {
      fail(childPath(path, key), "expected an enumerable data property");
    }
    return key;
  });

/**
 * @param {unknown} value
 * @param {ContractPath} path
 * @param {readonly string[]} requiredKeys
 * @param {readonly string[]} [optionalKeys]
 * @returns {JsonObject}
 */
const expectObject = (value, path, requiredKeys, optionalKeys = []) => {
  const record = expectRecord(value, path);
  const allowedKeys = new Set([...requiredKeys, ...optionalKeys]);
  ownJsonKeys(record, path).forEach(key => {
    if (!allowedKeys.has(key)) fail(childPath(path, key), "unexpected key");
  });
  requiredKeys.forEach(key => {
    if (!hasOwn(record, key)) fail(childPath(path, key), "missing required key");
  });
  return record;
};

/**
 * @param {unknown} value
 * @param {ContractPath} path
 * @returns {unknown[]}
 */
const expectArray = (value, path) => {
  if (!Array.isArray(value)) fail(path, "expected an array");
  const allowedKeys = new Set(["length"]);
  for (let index = 0; index < value.length; index += 1) {
    allowedKeys.add(String(index));
    const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
    if (!descriptor || !descriptor.enumerable || !("value" in descriptor) || descriptor.value === undefined) {
      fail(childPath(path, index), "expected a defined enumerable data element");
    }
  }
  Reflect.ownKeys(value).forEach(key => {
    if (typeof key !== "string") {
      fail(path, "symbol keys are not valid JSON array keys");
    } else if (!allowedKeys.has(key)) {
      fail(childPath(path, key), "unexpected array key");
    }
  });
  return /** @type {unknown[]} */ (value);
};

/**
 * @param {unknown[]} value
 * @param {ContractPath} path
 * @returns {void}
 */
const expectNonempty = (value, path) => {
  if (value.length === 0) fail(path, "expected at least one item");
};

/**
 * @param {unknown} value
 * @param {ContractPath} path
 * @param {boolean} [nonBlank]
 * @returns {string}
 */
const expectString = (value, path, nonBlank = false) => {
  if (typeof value !== "string" || (nonBlank && value.trim().length === 0)) {
    fail(path, nonBlank ? "expected a nonblank string" : "expected a string");
  }
  return value;
};

/**
 * @param {JsonObject} value
 * @param {string} key
 * @param {ContractPath} path
 * @param {boolean} [nonBlank]
 * @returns {void}
 */
const expectOptionalString = (value, key, path, nonBlank = false) => {
  if (hasOwn(value, key)) expectString(value[key], childPath(path, key), nonBlank);
};

/**
 * @param {unknown} value
 * @param {ContractPath} path
 * @param {number} minimum
 * @param {number} [maximum]
 * @returns {number}
 */
const expectInteger = (value, path, minimum, maximum = Number.MAX_SAFE_INTEGER) => {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < minimum || value > maximum) {
    fail(path, `expected an integer from ${minimum} to ${maximum}`);
  }
  return value;
};

/**
 * @param {unknown} value
 * @param {ContractPath} path
 * @returns {ElearningUuid}
 */
const expectUuid = (value, path) => {
  const uuid = expectString(value, path);
  if (!UUID_PATTERN.test(uuid)) fail(path, "expected a lowercase UUID");
  return uuid;
};

/**
 * @param {unknown} value
 * @param {ContractPath} path
 * @returns {ElearningHashV1}
 */
const expectHash = (value, path) => {
  const hash = expectString(value, path);
  if (!HASH_PATTERN.test(hash)) fail(path, "expected a sha256 hash");
  return /** @type {ElearningHashV1} */ (hash);
};

/**
 * @param {JsonObject} value
 * @param {ContractPath} path
 * @returns {void}
 */
const expectEnvelope = (value, path) => {
  if (value.schemaVersion !== 1) {
    fail(childPath(path, "schemaVersion"), "only schemaVersion 1 is supported");
  }
  if (value.locale !== ELEARNING_LOCALE) {
    fail(childPath(path, "locale"), `only locale ${ELEARNING_LOCALE} is supported`);
  }
};

/**
 * @param {string} filename
 * @returns {boolean}
 */
const isSafeElearningMediaFilename = filename => MEDIA_FILENAME_PATTERN.test(filename);

/**
 * @param {unknown} value
 * @param {ContractPath} path
 * @returns {void}
 */
const expectMedia = (value, path) => {
  const media = expectObject(value, path, ["fn"], ["alt"]);
  const filename = expectString(media.fn, childPath(path, "fn"), true);
  if (!isSafeElearningMediaFilename(filename)) {
    fail(childPath(path, "fn"), "expected a safe media filename");
  }
  expectOptionalString(media, "alt", path);
};

/**
 * @param {JsonObject} value
 * @param {string} key
 * @param {ContractPath} path
 * @returns {void}
 */
const expectOptionalMedia = (value, key, path) => {
  if (hasOwn(value, key)) expectMedia(value[key], childPath(path, key));
};

/**
 * @param {unknown} value
 * @param {ContractPath} path
 * @param {string} expected
 * @param {Set<string>} ids
 * @returns {void}
 */
const expectFullPathId = (value, path, expected, ids) => {
  const id = expectString(value, path);
  if (ids.has(id)) fail(path, `duplicate full-path child ID ${id}`);
  ids.add(id);
  if (id !== expected) fail(path, `expected deterministic ID ${expected}`);
};

/**
 * @param {unknown} value
 * @param {ContractPath} path
 * @returns {void}
 */
const expectTemplatePage = (value, path) => {
  const page = expectObject(value, path, ["kind", "text", "mediaWithCaption"], ["title"]);
  expectOptionalString(page, "title", path, true);
  const kind = expectString(page.kind, childPath(path, "kind"));
  if (!TEMPLATE_KINDS.has(kind)) {
    fail(childPath(path, "kind"), `unsupported template kind ${kind}`);
  }
  if (page.text !== null) expectString(page.text, childPath(path, "text"));

  let mediaCount = 0;
  if (page.mediaWithCaption !== null) {
    const mediaPath = childPath(path, "mediaWithCaption");
    const media = expectArray(page.mediaWithCaption, mediaPath);
    mediaCount = media.length;
    media.forEach((item, index) => {
      const itemPath = childPath(mediaPath, index);
      const wrapper = expectObject(item, itemPath, ["media", "caption"]);
      expectMedia(wrapper.media, childPath(itemPath, "media"));
      if (wrapper.caption !== null) expectString(wrapper.caption, childPath(itemPath, "caption"));
    });
  }

  const requireText = () => expectString(page.text, childPath(path, "text"), true);
  /** @param {number} expected */
  const requireMediaCount = expected => {
    if (mediaCount !== expected) {
      fail(
        childPath(path, "mediaWithCaption"),
        `template ${kind} must contain exactly ${expected} media item${expected === 1 ? "" : "s"}`
      );
    }
  };

  switch (kind) {
    case "textOnly":
    case "singlePage":
      requireText();
      requireMediaCount(0);
      return;
    case "imageFullscreen":
      requireMediaCount(1);
      return;
    case "twoImagesFullscreen":
      requireMediaCount(2);
      return;
    case "imageLeftTextRight":
    case "imageRightTextLeft":
    case "floatingImageRight":
    case "floatingImageLeft":
      requireText();
      requireMediaCount(1);
  }
};

/**
 * @param {string} value
 * @returns {string}
 */
const normalizeMatchingValue = value => value.trim().normalize("NFC").toLocaleLowerCase(ELEARNING_LOCALE);

/**
 * @param {unknown} value
 * @param {ContractPath} path
 * @param {string} expectedId
 * @param {Set<string>} ids
 * @returns {void}
 */
const expectLessonBlock = (value, path, expectedId, ids) => {
  const block = expectRecord(value, path);
  const type = expectString(block.type, childPath(path, "type"));
  switch (type) {
    case "template": {
      const item = expectObject(value, path, ["id", "type", "page"]);
      expectFullPathId(item.id, childPath(path, "id"), expectedId, ids);
      expectTemplatePage(item.page, childPath(path, "page"));
      return;
    }
    case "video": {
      const item = expectObject(value, path, ["id", "type", "title", "video"], ["thumbnail"]);
      expectFullPathId(item.id, childPath(path, "id"), expectedId, ids);
      expectString(item.title, childPath(path, "title"), true);
      expectMedia(item.video, childPath(path, "video"));
      expectOptionalMedia(item, "thumbnail", path);
      return;
    }
    case "gallery": {
      const item = expectObject(value, path, ["id", "type", "title", "photos"]);
      expectFullPathId(item.id, childPath(path, "id"), expectedId, ids);
      expectString(item.title, childPath(path, "title"), true);
      const photosPath = childPath(path, "photos");
      const photos = expectArray(item.photos, photosPath);
      expectNonempty(photos, photosPath);
      photos.forEach((photo, index) => {
        const photoPath = childPath(photosPath, index);
        const parsed = expectObject(photo, photoPath, ["id", "media"], ["caption"]);
        expectFullPathId(parsed.id, childPath(photoPath, "id"), `${expectedId}:photo:${index}`, ids);
        expectMedia(parsed.media, childPath(photoPath, "media"));
        expectOptionalString(parsed, "caption", photoPath, true);
      });
      return;
    }
    case "flipCards": {
      const item = expectObject(value, path, ["id", "type", "cards"]);
      expectFullPathId(item.id, childPath(path, "id"), expectedId, ids);
      const cardsPath = childPath(path, "cards");
      const cards = expectArray(item.cards, cardsPath);
      expectNonempty(cards, cardsPath);
      cards.forEach((card, index) => {
        const cardPath = childPath(cardsPath, index);
        const parsed = expectObject(card, cardPath, ["id", "title", "frontImage", "back"], ["icon"]);
        expectFullPathId(parsed.id, childPath(cardPath, "id"), `${expectedId}:card:${index}`, ids);
        expectString(parsed.title, childPath(cardPath, "title"), true);
        expectOptionalMedia(parsed, "icon", cardPath);
        expectMedia(parsed.frontImage, childPath(cardPath, "frontImage"));
        expectTemplatePage(parsed.back, childPath(cardPath, "back"));
      });
      return;
    }
    case "dialogue": {
      const item = expectObject(value, path, ["id", "type", "steps"], ["autoAdvanceMs"]);
      expectFullPathId(item.id, childPath(path, "id"), expectedId, ids);
      if (hasOwn(item, "autoAdvanceMs")) {
        expectInteger(item.autoAdvanceMs, childPath(path, "autoAdvanceMs"), 0);
      }
      const stepsPath = childPath(path, "steps");
      const steps = expectArray(item.steps, stepsPath);
      expectNonempty(steps, stepsPath);
      steps.forEach((step, index) => {
        const stepPath = childPath(stepsPath, index);
        const parsed = expectObject(step, stepPath, ["id", "speaker", "said"], ["feeling", "response"]);
        expectFullPathId(parsed.id, childPath(stepPath, "id"), `${expectedId}:step:${index}`, ids);
        expectString(parsed.speaker, childPath(stepPath, "speaker"), true);
        expectString(parsed.said, childPath(stepPath, "said"), true);
        expectOptionalString(parsed, "feeling", stepPath, true);
        expectOptionalString(parsed, "response", stepPath, true);
      });
      return;
    }
    case "matching": {
      const item = expectObject(value, path, ["id", "type", "pairs"]);
      expectFullPathId(item.id, childPath(path, "id"), expectedId, ids);
      const pairsPath = childPath(path, "pairs");
      const pairs = expectArray(item.pairs, pairsPath);
      if (pairs.length < 2) fail(pairsPath, "matching blocks must contain at least two pairs");
      /** @type {Set<string>} */
      const terms = new Set();
      /** @type {Set<string>} */
      const definitions = new Set();
      pairs.forEach((pair, index) => {
        const pairPath = childPath(pairsPath, index);
        const parsed = expectObject(pair, pairPath, ["id", "term", "definition"], ["image"]);
        expectFullPathId(parsed.id, childPath(pairPath, "id"), `${expectedId}:pair:${index}`, ids);
        const term = expectString(parsed.term, childPath(pairPath, "term"), true);
        const definition = expectString(parsed.definition, childPath(pairPath, "definition"), true);
        const normalizedTerm = normalizeMatchingValue(term);
        const normalizedDefinition = normalizeMatchingValue(definition);
        if (terms.has(normalizedTerm)) fail(childPath(pairPath, "term"), "matching terms must be unique");
        if (definitions.has(normalizedDefinition)) {
          fail(childPath(pairPath, "definition"), "matching definitions must be unique");
        }
        terms.add(normalizedTerm);
        definitions.add(normalizedDefinition);
        expectOptionalMedia(parsed, "image", pairPath);
      });
      return;
    }
    default:
      fail(childPath(path, "type"), `unsupported lesson block type ${type}`);
  }
};

/**
 * @param {unknown} value
 * @param {ContractPath} path
 * @param {string} [expectedMapId]
 * @returns {ElearningCourseV1}
 */
const parseCourseAt = (value, path, expectedMapId) => {
  const course = expectObject(value, path, [
    "schemaVersion",
    "locale",
    "id",
    "version",
    "title",
    "description",
    "thumbnail",
    "estimatedDurationMinutes",
    "tags",
    "lessons",
    "finalQuiz",
  ]);
  expectEnvelope(course, path);
  const id = expectUuid(course.id, childPath(path, "id"));
  if (expectedMapId !== undefined && id !== expectedMapId) {
    fail(childPath(path, "id"), `course ID must match map key ${expectedMapId}`);
  }
  expectHash(course.version, childPath(path, "version"));
  expectString(course.title, childPath(path, "title"), true);
  expectString(course.description, childPath(path, "description"), true);
  expectMedia(course.thumbnail, childPath(path, "thumbnail"));
  expectInteger(course.estimatedDurationMinutes, childPath(path, "estimatedDurationMinutes"), 1);

  const tagsPath = childPath(path, "tags");
  /** @type {Set<string>} */
  const normalizedTags = new Set();
  expectArray(course.tags, tagsPath).forEach((tag, index) => {
    const tagPath = childPath(tagsPath, index);
    const parsed = expectString(tag, tagPath, true).replace(/\r\n?/g, "\n").normalize("NFC");
    if (normalizedTags.has(parsed)) fail(tagPath, "course tags must be unique");
    normalizedTags.add(parsed);
  });

  /** @type {Set<string>} */
  const childIds = new Set();
  /** @type {Set<string>} */
  const lessonIds = new Set();
  const lessonsPath = childPath(path, "lessons");
  const lessons = expectArray(course.lessons, lessonsPath);
  expectNonempty(lessons, lessonsPath);
  lessons.forEach((lesson, lessonIndex) => {
    const lessonPath = childPath(lessonsPath, lessonIndex);
    const parsed = expectObject(lesson, lessonPath, ["id", "title", "blocks"]);
    const lessonId = expectUuid(parsed.id, childPath(lessonPath, "id"));
    if (lessonIds.has(lessonId)) fail(childPath(lessonPath, "id"), `duplicate lesson ID ${lessonId}`);
    lessonIds.add(lessonId);
    expectString(parsed.title, childPath(lessonPath, "title"), true);
    const blocksPath = childPath(lessonPath, "blocks");
    const blocks = expectArray(parsed.blocks, blocksPath);
    expectNonempty(blocks, blocksPath);
    blocks.forEach((block, blockIndex) =>
      expectLessonBlock(block, childPath(blocksPath, blockIndex), `lesson:${lessonId}:block:${blockIndex}`, childIds)
    );
  });

  const quizPath = childPath(path, "finalQuiz");
  const quiz = expectObject(course.finalQuiz, quizPath, ["passingScorePercent", "questions"]);
  expectInteger(quiz.passingScorePercent, childPath(quizPath, "passingScorePercent"), 0, 100);
  const questionsPath = childPath(quizPath, "questions");
  const questions = expectArray(quiz.questions, questionsPath);
  expectNonempty(questions, questionsPath);
  questions.forEach((question, questionIndex) => {
    const questionPath = childPath(questionsPath, questionIndex);
    const parsed = expectObject(question, questionPath, ["id", "prompt", "answers", "explanation"], ["image"]);
    const expectedQuestionId = `quiz:question:${questionIndex}`;
    expectFullPathId(parsed.id, childPath(questionPath, "id"), expectedQuestionId, childIds);
    expectString(parsed.prompt, childPath(questionPath, "prompt"), true);
    expectOptionalMedia(parsed, "image", questionPath);
    const answersPath = childPath(questionPath, "answers");
    const answers = expectArray(parsed.answers, answersPath);
    if (answers.length < 2) fail(answersPath, "expected at least two answers");
    let correctAnswers = 0;
    answers.forEach((answer, answerIndex) => {
      const answerPath = childPath(answersPath, answerIndex);
      const parsedAnswer = expectObject(answer, answerPath, ["id", "text", "correct"], ["image"]);
      expectFullPathId(
        parsedAnswer.id,
        childPath(answerPath, "id"),
        `${expectedQuestionId}:answer:${answerIndex}`,
        childIds
      );
      expectString(parsedAnswer.text, childPath(answerPath, "text"), true);
      expectOptionalMedia(parsedAnswer, "image", answerPath);
      if (typeof parsedAnswer.correct !== "boolean") fail(childPath(answerPath, "correct"), "expected a boolean");
      if (parsedAnswer.correct) correctAnswers += 1;
    });
    if (correctAnswers !== 1) fail(answersPath, "quiz questions must contain exactly one correct answer");
    expectTemplatePage(parsed.explanation, childPath(questionPath, "explanation"));
  });

  return /** @type {ElearningCourseV1} */ (value);
};

/**
 * @param {unknown} value
 * @returns {ElearningCourseV1}
 */
const parseElearningCourseV1 = value => parseCourseAt(value, []);

/**
 * @param {unknown} value
 * @param {ContractPath} path
 * @param {string} [expectedMapId]
 * @returns {ElearningLearningPathV1}
 */
const parseLearningPathAt = (value, path, expectedMapId) => {
  const learningPath = expectObject(value, path, [
    "schemaVersion",
    "locale",
    "id",
    "version",
    "title",
    "description",
    "thumbnail",
    "enforceOrder",
    "courses",
  ]);
  expectEnvelope(learningPath, path);
  const id = expectUuid(learningPath.id, childPath(path, "id"));
  if (expectedMapId !== undefined && id !== expectedMapId) {
    fail(childPath(path, "id"), `learning-path ID must match map key ${expectedMapId}`);
  }
  expectHash(learningPath.version, childPath(path, "version"));
  expectString(learningPath.title, childPath(path, "title"), true);
  expectString(learningPath.description, childPath(path, "description"), true);
  expectMedia(learningPath.thumbnail, childPath(path, "thumbnail"));
  if (learningPath.enforceOrder !== true) {
    fail(childPath(path, "enforceOrder"), "learning paths must enforce order");
  }

  const coursesPath = childPath(path, "courses");
  const courses = expectArray(learningPath.courses, coursesPath);
  expectNonempty(courses, coursesPath);
  /** @type {Set<string>} */
  const courseIds = new Set();
  courses.forEach((course, index) => {
    const coursePath = childPath(coursesPath, index);
    const reference = expectObject(course, coursePath, ["courseId", "position"]);
    const courseId = expectUuid(reference.courseId, childPath(coursePath, "courseId"));
    if (courseIds.has(courseId)) fail(childPath(coursePath, "courseId"), `duplicate course ${courseId}`);
    courseIds.add(courseId);
    const position = expectInteger(reference.position, childPath(coursePath, "position"), 0);
    if (position !== index) fail(childPath(coursePath, "position"), "positions must be contiguous from zero");
  });
  return /** @type {ElearningLearningPathV1} */ (value);
};

/**
 * @param {unknown} value
 * @returns {ElearningLearningPathV1}
 */
const parseElearningLearningPathV1 = value => parseLearningPathAt(value, []);

/**
 * @param {unknown} value
 * @returns {ElearningCourseMapV1}
 */
const parseElearningCourseMapV1 = value => {
  const courses = expectRecord(value, []);
  ownJsonKeys(courses, []).forEach(mapKey => {
    expectUuid(mapKey, [mapKey]);
    parseCourseAt(courses[mapKey], [mapKey], mapKey);
  });
  return /** @type {ElearningCourseMapV1} */ (value);
};

/**
 * @param {unknown} value
 * @param {ElearningCourseMapV1} [courses]
 * @returns {ElearningLearningPathMapV1}
 */
const parseElearningLearningPathMapV1 = (value, courses) => {
  const learningPaths = expectRecord(value, []);
  ownJsonKeys(learningPaths, []).forEach(mapKey => {
    expectUuid(mapKey, [mapKey]);
    const learningPath = parseLearningPathAt(learningPaths[mapKey], [mapKey], mapKey);
    if (courses) {
      learningPath.courses.forEach((reference, index) => {
        if (!hasOwn(courses, reference.courseId)) {
          fail([mapKey, "courses", index, "courseId"], `referenced course ${reference.courseId} is missing`);
        }
      });
    }
  });
  return /** @type {ElearningLearningPathMapV1} */ (value);
};

/**
 * @param {unknown} coursesValue
 * @param {unknown} learningPathsValue
 * @returns {ElearningCollectionsV1}
 */
const parseElearningCollectionsV1 = (coursesValue, learningPathsValue) => {
  const courses = parseElearningCourseMapV1(coursesValue);
  const learningPaths = parseElearningLearningPathMapV1(learningPathsValue, courses);
  return { courses, learningPaths };
};

/**
 * @param {string} payload
 * @returns {ElearningCourseV1}
 */
const parseLearningCoursePayload = payload => {
  let value;
  try {
    value = JSON.parse(payload);
  } catch (_error) {
    fail([], "expected valid JSON");
  }
  return parseElearningCourseV1(value);
};

/**
 * @param {LearningHtmlSanitizer} sanitizeHtml
 * @returns {(html: string) => string}
 */
const createElearningHtmlSanitizer = sanitizeHtml => html => sanitizeHtml(html, LEARNING_HTML_OPTIONS);

/**
 * @param {string} url
 * @returns {boolean}
 */
const isInlineTemplateDocumentUrl = url => url === "about:blank";

/**
 * @param {string} url
 * @returns {boolean}
 */
const isSafeExternalTemplateUrl = url => WEB_LINK_PATTERN.test(url);

/**
 * @param {ElearningMediaRefV1} media
 * @param {ResolveElearningMedia} resolveMedia
 * @returns {string | null}
 */
const resolveElearningMediaRef = (media, resolveMedia) => {
  if (!isSafeElearningMediaFilename(media.fn)) return null;
  const resolvedUri = resolveMedia(media.fn);
  return resolvedUri && RESOLVED_MEDIA_URI_PATTERN.test(resolvedUri) ? resolvedUri : null;
};

/**
 * @template TKind
 * @param {ElearningTemplatePageV1} page
 * @param {ResolveElearningMedia} resolveMedia
 * @param {(text: string) => string} sanitizeText
 * @param {(kind: ElearningTemplateKindV1) => TKind} mapKind
 * @returns {import("./learning").SafeElearningTemplatePage<TKind> | null}
 */
const createSafeElearningTemplatePage = (page, resolveMedia, sanitizeText, mapKind) => {
  /** @type {Array<{media: {url: string}; caption: string | null}>} */
  const mediaWithCaption = [];
  for (const item of page.mediaWithCaption || []) {
    const resolvedUri = resolveElearningMediaRef(item.media, resolveMedia);
    if (!resolvedUri) return null;
    mediaWithCaption.push({
      media: { url: resolvedUri },
      caption: item.caption,
    });
  }

  return {
    ...(page.title === undefined ? {} : { title: page.title }),
    kind: mapKind(page.kind),
    text: page.text === null ? null : sanitizeText(page.text),
    mediaWithCaption: page.mediaWithCaption === null ? null : mediaWithCaption,
  };
};

/** @type {typeof import("./learning")} */
module.exports = {
  ELEARNING_LOCALE,
  ElearningContentContractError,
  LearningContentContractError: ElearningContentContractError,
  LEARNING_LOCALE: ELEARNING_LOCALE,
  createElearningHtmlSanitizer,
  createSafeElearningTemplatePage,
  isInlineTemplateDocumentUrl,
  isSafeElearningMediaFilename,
  isSafeExternalTemplateUrl,
  parseElearningCollectionsV1,
  parseElearningCourseMapV1,
  parseElearningCourseV1,
  parseElearningLearningPathMapV1,
  parseElearningLearningPathV1,
  parseLearningCourse: parseElearningCourseV1,
  parseLearningCoursePayload,
  parseLearningPath: parseElearningLearningPathV1,
  resolveElearningMediaRef,
};
